// Die Cut Studio's dies, drawn as their own builders draw them
// (www.diecutstudio.com, its `head` chunk): lengths in mm, x right and y down,
// their lengths clamped as theirs are. Their L, W and H are a box's front,
// side and height, our height, depth and width; a tray's base and walls, our
// width, height and depth; an envelope's face, our width and height. Their
// grows' trees (DieCutStudioGrowList.ts) fold them.

import type { Distance } from "@pdfking/math"
import { Max, Min, PI, Sqrt } from "@pdfking/math"
import { MM } from "@pdfking/quantity"
import { NullQ } from "@pdfking/structs"

import { DieCutStudioBoard, DieCutStudioTongue, FolderPen } from "#internal"
import type { FolderDie, FolderPath, FolderTemplate, PrimaryKey } from "#public"
import { RecordByPrimaryKey } from "#public"

const die = <I extends string>(
  id: I,
  size: [Distance, Distance],
  draw: FolderTemplate["draw"]
): FolderTemplate & PrimaryKey<I> => ({ id, size, draw })

// Their inches
const inch = (v: number) => v * DieCutStudioBoard.inch
const clamp = (v: number, lo: number, hi: number) => Max(lo, Min(hi, v))
const mm = (d: Distance) => d / MM(1)

const line = (x0: number, y0: number, x1: number, y1: number): FolderPath => {
  const pen = FolderPen()
  pen.line(x0, y0)
  pen.line(x1, y1)
  return pen.path(false)
}

// The die `width` x `height` of its cuts and creases.
const drawn = (width: number, height: number, cut: FolderPath[], fold: FolderPath[]) =>
  ({ width: MM(width), height: MM(height), cut, fold }) as FolderDie

// A tuck end's tongue
const tongue_of = (L: number, W: number) =>
  DieCutStudioTongue(L, W, {
    min: (a, b) => Min(a, b),
    max: (a, b) => Max(a, b),
    add: (a, b) => a + b,
    times: (a, f) => a * f,
    inch,
  })

// Straight Tuck End: a tuck top and bottom, a thumb notch on the front at either
const bxte = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const tongue = tongue_of(L, W)
  const glue = Min(inch(0.5), W * 0.9)
  const dust = Min(W * 0.85, L * 0.45)
  const [dc, tc, gt, rN] = [
    Min(dust * 0.5, W * 0.25),
    Min(tongue * 0.7, L * 0.25),
    Min(H * 0.1, inch(0.2)),
    Min(inch(0.4), L * 0.18),
  ]
  const f = W + tongue
  const d = f + H
  const [m, M, P, v, k] = [glue, glue + L, glue + L + W, glue + 2 * L + W, glue + 2 * L + 2 * W]
  const [w, g, C] = [f - W, f - W - tongue, f - dust]
  const [Lb, Wb, S] = [d + W, d + W + tongue, d + dust]
  const notch = P + L / 2
  const pen = FolderPen()
  const points: [number, number][] = [
    [0, f + gt],
    [gt, f],
    [m, f],
    [m, w],
    [m + tc, g],
    [M - tc, g],
    [M, w],
    [M, f],
    [M, C + dc],
    [M + dc, C],
    [P - dc, C],
    [P, C + dc],
    [P, f],
    [notch - rN, f],
  ]
  for (const [x, y] of points) pen.line(x, y)
  pen.arc(notch, f, rN, PI, 0)
  for (const [x, y] of [
    [v, f],
    [v, C + dc],
    [v + dc, C],
    [k - dc, C],
    [k, C + dc],
    [k, f],
    [k, d],
    [k, S - dc],
    [k - dc, S],
    [v + dc, S],
    [v, S - dc],
    [v, d],
    [notch + rN, d],
  ] as [number, number][])
    pen.line(x, y)
  pen.arc(notch, d, rN, 0, -PI)
  for (const [x, y] of [
    [P, d],
    [P, S - dc],
    [P - dc, S],
    [M + dc, S],
    [M, S - dc],
    [M, d],
    [M, Lb],
    [M - tc, Wb],
    [m + tc, Wb],
    [m, Lb],
    [m, d],
    [gt, d],
    [0, d - gt],
  ] as [number, number][])
    pen.line(x, y)
  return drawn(
    k,
    2 * f + H,
    [pen.path(true)],
    [
      ...[m, M, P, v].map(x => line(x, f, x, d)),
      ...[
        [m, M],
        [M, P],
        [v, k],
      ].flatMap(([a, b]) => [line(a!, f, b!, f), line(a!, d, b!, d)]),
      line(m, w, M, w),
      line(m, Lb, M, Lb),
    ]
  )
}

// A tuck top, the tongue off the back, its dust flaps and the front's thumb
// notch, over `bottom`: the bottom's corners from the glue tab round, x and
// y, and its creases, given where the walls are and how deep the top goes
type Tube = {
  L: number
  W: number
  H: number
  glue: number
  dust: number
  tongue: number
}
const tuck_top = (
  { L, W, H, glue, dust, tongue }: Tube,
  bottom: (walls: number[], top: number, base: number, gt: number) => [number, number][]
): { cut: FolderPath; walls: number[]; top: number; base: number } => {
  const [tc, dc, gt, rN] = [
    Min(tongue * 0.7, L * 0.25),
    Min(dust * 0.5, W * 0.25),
    Min(H * 0.1, inch(0.2)),
    Min(inch(0.4), L * 0.18),
  ]
  const [M, P, v, k, w] = [glue, glue + L, glue + L + W, glue + 2 * L + W, glue + 2 * L + 2 * W]
  const top = W + tongue
  const base = top + H
  const [S, T, Hy] = [top - W, top - W - tongue, top - dust]
  const notch = v + L / 2
  const pen = FolderPen()
  for (const [x, y] of [
    [0, top + gt],
    [gt, top],
    [M, top],
    [M, S],
    [M + tc, T],
    [P - tc, T],
    [P, S],
    [P, top],
    [P, Hy + dc],
    [P + dc, Hy],
    [v - dc, Hy],
    [v, Hy + dc],
    [v, top],
    [notch - rN, top],
  ] as [number, number][])
    pen.line(x, y)
  pen.arc(notch, top, rN, PI, 0)
  for (const [x, y] of [
    [k, top],
    [k, Hy + dc],
    [k + dc, Hy],
    [w - dc, Hy],
    [w, Hy + dc],
    [w, top],
    ...bottom([M, P, v, k, w], top, base, gt),
  ] as [number, number][])
    pen.line(x, y)
  return { cut: pen.path(true), walls: [M, P, v, k, w], top, base }
}

// Tuck Top Auto Bottom: the tuck top over a crash-lock bottom
const bxtab = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const tube: Tube = {
    L,
    W,
    H,
    glue: clamp(W * 0.5, 10, 20),
    dust: Min(W * 0.85, L * 0.45),
    tongue: tongue_of(L, W),
  }
  const [major, minor] = [W / 2, L / 2]
  const p = Max(DieCutStudioBoard.thickness, 0.8) / 2
  const { cut, walls, top, base } = tuck_top(tube, ([M, P, v, k, w], _, base, gt) => [
    [w!, base],
    [w!, base + minor],
    [k! + p, base + minor],
    [k! + p, base],
    [k! - p, base],
    [k! - p, base + major],
    [v! + p, base + major],
    [v! + p, base],
    [v! - p, base],
    [v! - p, base + minor],
    [P! + p, base + minor],
    [P! + p, base],
    [P! - p, base],
    [P! - p, base + major],
    [M!, base + major],
    [M!, base],
    [gt, base],
    [0, base - gt],
  ])
  const [M, P, v, k, w] = walls as [number, number, number, number, number]
  return drawn(
    w,
    base + Max(major, minor),
    [cut],
    [
      ...[M, P, v, k].map(x => line(x, top, x, base)),
      line(M, top, P, top),
      line(P, top, v, top),
      line(k, top, w, top),
      line(M, top - W, P, top - W),
      line(M, base, P - p, base),
      line(P + p, base, v - p, base),
      line(v + p, base, k - p, base),
      line(k + p, base, w, base),
      line(P - p, base, P - p - major, base + major),
      line(k - p, base, k - p - major, base + major),
    ]
  )
}

// Tuck-Top Snap-Lock: the tuck top over a 1-2-3 bottom, the front's lock tab
// snapping it shut
const bxsl = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const tube: Tube = {
    L,
    W,
    H,
    glue: clamp(W * 0.5, 10, 20),
    dust: Min(W * 0.85, L * 0.45),
    tongue: tongue_of(L, W),
  }
  const [major, minor] = [W / 2, L / 2]
  const [tab, tab_length] = [clamp(L * 0.4, 18, L * 0.7), Min(W * 0.22, 14)]
  const clip = Min(minor * 0.35, major * 0.7)
  const p = Max(DieCutStudioBoard.thickness, 0.8) / 2
  const { cut, walls, top, base } = tuck_top(tube, ([M, P, v, k, w], _, base, gt) => {
    const middle = v! + L / 2
    return [
      [w!, base],
      [w!, base + minor],
      [k! + p + clip, base + minor],
      [k! + p, base + minor - clip],
      [k! + p, base],
      [k! - p, base],
      [k! - p, base + major],
      [middle + tab / 2, base + major],
      [middle + tab / 2, base + major + tab_length],
      [middle - tab / 2, base + major + tab_length],
      [middle - tab / 2, base + major],
      [v! + p, base + major],
      [v! + p, base],
      [v! - p, base],
      [v! - p, base + minor - clip],
      [v! - p - clip, base + minor],
      [P! + p, base + minor],
      [P! + p, base],
      [P! - p, base],
      [P! - p, base + major],
      [M!, base + major],
      [M!, base],
      [gt, base],
      [0, base - gt],
    ]
  })
  const [M, P, v, k, w] = walls as [number, number, number, number, number]
  return drawn(
    w,
    base + Max(minor, major + tab_length),
    [cut],
    [
      ...[M, P, v, k].map(x => line(x, top, x, base)),
      line(M, top, P, top),
      line(P, top, v, top),
      line(k, top, w, top),
      line(M, top - W, P, top - W),
      line(M, base, P - p, base),
      line(P + p, base, v - p, base),
      line(v + p, base, k - p, base),
      line(k + p, base, w, base),
    ]
  )
}

// Hexagon Box: six walls round an elongated hexagon, L its long axis and W
// its short, a pointed infold flap off every wall top and bottom
const bxhx = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const glue = Min(inch(0.5), W * 0.6)
  const relief = Max(DieCutStudioBoard.thickness, 1.5)
  // The hexagon's walls: the long ones, the slanted ones, each its width and
  // how far its middle is from the centre
  const end = Min(W / 2, L * 0.4)
  const long = L - 2 * end
  const slant = Sqrt(end ** 2 + W ** 2 / 4)
  const slant_apo = (W * (long / 2 + end)) / (2 * slant)
  const hexagon = [
    { w: long, apo: W / 2 },
    { w: slant, apo: slant_apo },
    { w: slant, apo: slant_apo },
    { w: long, apo: W / 2 },
    { w: slant, apo: slant_apo },
    { w: slant, apo: slant_apo },
  ]
  const tip = Min(Min(...hexagon.map(wall => wall.apo)) * 0.18, 6)
  const top = Max(...hexagon.map(wall => wall.apo)) + tip
  const base = top + H
  const xs = hexagon.reduce((at, wall) => [...at, at[at.length - 1]! + wall.w], [glue])
  const right = xs[xs.length - 1]!
  const gt = Min(H * 0.1, inch(0.2))
  const pen = FolderPen()
  pen.line(0, top + gt)
  pen.line(gt, top)
  pen.line(glue, top)
  hexagon.forEach((wall, i) => {
    const [a, b] = [xs[i]!, xs[i + 1]!]
    pen.line(a + relief / 2, top)
    pen.line((a + b) / 2, top - wall.apo - tip)
    pen.line(b - relief / 2, top)
    pen.line(b, top)
  })
  pen.line(right, base)
  for (let i = hexagon.length - 1; i >= 0; i--) {
    const [a, b] = [xs[i]!, xs[i + 1]!]
    pen.line(b - relief / 2, base)
    pen.line((a + b) / 2, base + hexagon[i]!.apo + tip)
    pen.line(a + relief / 2, base)
    pen.line(a, base)
  }
  pen.line(gt, base)
  pen.line(0, base - gt)
  return drawn(
    right,
    2 * top + H,
    [pen.path(true)],
    [
      ...xs.slice(0, -1).map(x => line(x, top, x, base)),
      ...hexagon.flatMap((_, i) => [
        line(xs[i]!, top, xs[i + 1]!, top),
        line(xs[i]!, base, xs[i + 1]!, base),
      ]),
    ]
  )
}

// A handle's slot, `length` long and `height` high, its ends round, centred
// on `x`, `y`
const slot = (x: number, y: number, length: number, height: number): FolderPath => {
  const r = height / 2
  const [a, b] = [x - length / 2 + r, x + length / 2 - r]
  const pen = FolderPen()
  pen.line(a, y - r)
  pen.line(b, y - r)
  pen.arc(b, y, r, -PI / 2, PI / 2)
  pen.line(a, y + r)
  pen.arc(a, y, r, PI / 2, 1.5 * PI)
  return pen.path(true)
}

// Gable Box: a roof off the front and the back, a gable off either side,
// the handle's slots through the roofs and its lock slits through the gables
const bxgb = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const gable = W / 2
  const glue = Min(inch(0.5), W * 0.9)
  const tongue = Min(W * 0.5, inch(0.7))
  const dust = Min(W * 0.85, L * 0.45)
  const handle = clamp(L * 0.42, 40, L - 12)
  const handle_height = 14
  const roof = Sqrt(gable ** 2 + W ** 2 / 4)
  const ear = Min(11, roof * 0.32)
  const ear_width = clamp(L * 0.13, 10, L * 0.24)
  const ear_cut = Min(ear_width * 0.35, ear * 0.55)
  const top = ear + roof
  const base = top + H
  const [k, w, g, C, right] = [glue, glue + L, glue + L + W, glue + 2 * L + W, glue + 2 * L + 2 * W]
  const eave = top - roof
  const apex = top - gable
  const [first, second] = [(w + g) / 2, (C + right) / 2]
  const [tc, dc, gt] = [
    Min(tongue * 0.7, L * 0.25),
    Min(dust * 0.5, W * 0.25),
    Min(H * 0.1, inch(0.2)),
  ]
  const [dusted, bottom, tip] = [base + dust, base + W, base + W + tongue]
  const pen = FolderPen()
  const ears = (a: number, b: number) => {
    for (const [x, y] of [
      [a, eave - ear + ear_cut],
      [a + ear_cut, eave - ear],
      [a + ear_width, eave - ear],
      [a + ear_width, eave],
      [b - ear_width, eave],
      [b - ear_width, eave - ear],
      [b - ear_cut, eave - ear],
      [b, eave - ear + ear_cut],
      [b, eave],
    ] as [number, number][])
      pen.line(x, y)
  }
  pen.line(0, top + gt)
  pen.line(gt, top)
  pen.line(k, top)
  pen.line(k, eave)
  ears(k, w)
  pen.line(w, top)
  pen.line(first, apex)
  pen.line(g, top)
  pen.line(g, eave)
  ears(g, C)
  for (const [x, y] of [
    [C, top],
    [second, apex],
    [right, top],
    [right, base],
    [right, dusted - dc],
    [right - dc, dusted],
    [C + dc, dusted],
    [C, dusted - dc],
    [C, base],
    [g, base],
    [g, dusted - dc],
    [g - dc, dusted],
    [w + dc, dusted],
    [w, dusted - dc],
    [w, base],
    [w, bottom],
    [w - tc, tip],
    [k + tc, tip],
    [k, bottom],
    [k, base],
    [gt, base],
    [0, base - gt],
  ] as [number, number][])
    pen.line(x, y)
  const handle_y = eave + 12 + handle_height / 2
  const from_apex = clamp(gable * 0.28, 7, gable * 0.5)
  const room = Max(3, gable - from_apex - 5)
  const slit = clamp(gable * 0.4, 3, room)
  return drawn(
    right,
    ear + roof + H + W + tongue,
    [
      pen.path(true),
      slot((k + w) / 2, handle_y, handle, handle_height),
      slot((g + C) / 2, handle_y, handle, handle_height),
      line(first, apex + from_apex, first, apex + from_apex + slit),
      line(second, apex + from_apex, second, apex + from_apex + slit),
    ],
    [
      ...[k, w, g, C].map(x => line(x, top, x, base)),
      ...[
        [k, w],
        [w, g],
        [g, C],
        [C, right],
      ].map(([a, b]) => line(a!, top, b!, top)),
      line(k, eave, k + ear_width, eave),
      line(w - ear_width, eave, w, eave),
      line(g, eave, g + ear_width, eave),
      line(C - ear_width, eave, C, eave),
      line(k, base, w, base),
      line(w, base, g, base),
      line(C, base, right, base),
      line(k, bottom, w, bottom),
    ]
  )
}

// An envelope's face `x0`..`x1` by `y0`..`y1`, its edges creased `inset`
// in from its corners, and each gusset `gusset` out past it creased too
const face_creases = (
  [x0, x1, y0, y1]: [number, number, number, number],
  inset: number,
  gusset: number
): FolderPath[] => [
  line(x0 + inset, y0, x1 - inset, y0),
  line(x1, y0 + inset, x1, y1 - inset),
  line(x0 + inset, y1, x1 - inset, y1),
  line(x0, y0 + inset, x0, y1 - inset),
  ...(gusset > 0
    ? [
        line(x0 + inset, y0 - gusset, x1 - inset, y0 - gusset),
        line(x1 + gusset, y0 + inset, x1 + gusset, y1 - inset),
        line(x0 + inset, y1 + gusset, x1 - inset, y1 + gusset),
        line(x0 - gusset, y0 + inset, x0 - gusset, y1 - inset),
      ]
    : []),
]

// Envelope (diamond): a pointed flap off every side of the face, on a
// gusset as deep as the envelope, their points round, the face's corners cut
// back between them
const envd = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, g] = [mm(width), mm(height), mm(depth)]
  const side = L * (90 / 229)
  const [above, below] = [W * (135 / 162), W * (90 / 162)]
  const point = L * (70 / 229)
  const least = Min(side, above, below)
  const corner = clamp(3, 0, least * 0.25)
  const round = clamp(15, 2, least * 0.42)
  const round_bottom = clamp(10, 2, point * 0.42)
  const [x0, y0] = [side + g, above + g]
  const [x1, y1] = [x0 + L, y0 + W]
  const [right, bottom] = [x1 + g + side, y1 + g + below]
  const [middle, centre] = [x0 + L / 2, y0 + W / 2]
  const pen = FolderPen()
  // Round `at`, coming from towards `from` and going towards `to`
  const rounded = (
    at: [number, number],
    from: [number, number],
    to: [number, number],
    r: number
  ) => {
    const unit = (q: [number, number]) => {
      const length = Sqrt((q[0] - at[0]) ** 2 + (q[1] - at[1]) ** 2) || 1
      return [(q[0] - at[0]) / length, (q[1] - at[1]) / length]
    }
    const [a, b] = [unit(from), unit(to)]
    pen.line(at[0] + a[0]! * r, at[1] + a[1]! * r)
    pen.quad(at[0] + b[0]! * r, at[1] + b[1]! * r, at[0], at[1])
  }
  // Each flap's base, past its gusset
  const [top_left, top_right]: [number, number][] = [
    [x0 + corner, y0 - g],
    [x1 - corner, y0 - g],
  ]
  const [right_top, right_bottom]: [number, number][] = [
    [x1 + g, y0 + corner],
    [x1 + g, y1 - corner],
  ]
  const [bottom_right, bottom_left]: [number, number][] = [
    [x1 - corner, y1 + g],
    [x0 + corner, y1 + g],
  ]
  const [left_bottom, left_top]: [number, number][] = [
    [x0 - g, y1 - corner],
    [x0 - g, y0 + corner],
  ]
  const [tip_right, tip_left]: [number, number][] = [
    [middle + point / 2, bottom],
    [middle - point / 2, bottom],
  ]
  pen.line(x0 + corner, y0)
  pen.line(...top_left)
  rounded([middle, 0], top_left, top_right, round)
  pen.line(...top_right)
  pen.line(x1 - corner, y0)
  pen.line(x1, y0 + corner)
  pen.line(...right_top)
  rounded([right, centre], right_top, right_bottom, round)
  pen.line(...right_bottom)
  pen.line(x1, y1 - corner)
  pen.line(x1 - corner, y1)
  pen.line(...bottom_right)
  rounded(tip_right, bottom_right, tip_left, round_bottom)
  rounded(tip_left, tip_right, bottom_left, round_bottom)
  pen.line(...bottom_left)
  pen.line(x0 + corner, y1)
  pen.line(x0, y1 - corner)
  pen.line(...left_bottom)
  rounded([0, centre], left_bottom, left_top, round)
  pen.line(...left_top)
  pen.line(x0, y0 + corner)
  return drawn(right, bottom, [pen.path(true)], face_creases([x0, x1, y0, y1], corner, g))
}

// A closed path through `points`, each reached straight, or round the
// control after it as a quadratic
type Traced = [x: number, y: number] | [x: number, y: number, cx: number, cy: number]
const trace = (points: Traced[]): FolderPath => {
  const pen = FolderPen()
  for (const [x, y, cx, cy] of points)
    if (NullQ(cx) || NullQ(cy)) pen.line(x, y)
    else pen.quad(x, y, cx, cy)
  return pen.path(true)
}

// Reverse Tuck End: the top tuck off the back, the bottom one off the front,
// each round at its corners, its shoulders cut in
const bxrt = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const glue = clamp(W * 0.8, 8, 20)
  const tuck_in = clamp(W * 0.5, 8, H * 0.5)
  const dust = W - 2
  const r = clamp(Min(W * 0.4, 12), 1.5, tuck_in * 0.45)
  const shoulder = Min(0.3 * W, 8, L / 4, 0.9 * (tuck_in - r))
  const dr = Min(W * 0.25, 4)
  const taper = Min(H * 0.08, 4)
  const [p, m, M, P, v] = [glue, glue + L, glue + L + W, glue + 2 * L + W, glue + 2 * L + 2 * W]
  const top = W + tuck_in
  const base = top + H
  const [tuck, Lb, Wb] = [top - W, base + W, base + W + tuck_in]
  const [S, T] = [top - dust, base + dust]
  return drawn(
    v,
    Wb,
    [
      trace([
        [0, top + taper],
        [taper, top],
        [p, top],
        [p, tuck],
        [p + shoulder, tuck - shoulder],
        [p + shoulder, r],
        [p + shoulder + r, 0, p + shoulder, 0],
        [m - shoulder - r, 0],
        [m - shoulder, r, m - shoulder, 0],
        [m - shoulder, tuck - shoulder],
        [m, tuck],
        [m, top],
        [m, S + dr],
        [m + dr, S],
        [M - dr, S],
        [M, S + dr],
        [M, top],
        [P, top],
        [P, S + dr],
        [P + dr, S],
        [v - dr, S],
        [v, S + dr],
        [v, top],
        [v, base],
        [v, T - dr],
        [v - dr, T],
        [P + dr, T],
        [P, T - dr],
        [P, base],
        [P, Lb],
        [P - shoulder, Lb + shoulder],
        [P - shoulder, Wb - r],
        [P - shoulder - r, Wb, P - shoulder, Wb],
        [M + shoulder + r, Wb],
        [M + shoulder, Wb - r, M + shoulder, Wb],
        [M + shoulder, Lb + shoulder],
        [M, Lb],
        [M, base],
        [M, T - dr],
        [M - dr, T],
        [m + dr, T],
        [m, T - dr],
        [m, base],
        [p, base],
        [taper, base],
        [0, base - taper],
      ]),
    ],
    [
      ...[p, m, M, P].map(x => line(x, top, x, base)),
      line(p, top, m, top),
      line(p, tuck, m, tuck),
      line(m, top, M, top),
      line(P, top, v, top),
      line(m, base, M, base),
      line(M, base, P, base),
      line(M, Lb, P, Lb),
      line(P, base, v, base),
    ]
  )
}

// Auto-Bottom Mailer: a tuck top, round at its corners, over a crash-lock
// bottom
const bxab = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const glue = clamp(W * 0.5, 10, 20)
  const tuck = W * 0.85
  const dust = W - 2
  const shoulder = Min(W * 0.3, 8)
  const r = Min(W * 0.4, 12)
  const dr = Min(W * 0.25, 4)
  const taper = Min(H * 0.1, inch(0.2))
  const [major, minor] = [W / 2, L / 2]
  const y = Max(DieCutStudioBoard.thickness, 0.8) / 2
  const [m, M, P, v, k] = [glue, glue + L, glue + L + W, glue + 2 * L + W, glue + 2 * L + 2 * W]
  const [top, base] = [tuck, tuck + H]
  const S = top - dust
  return drawn(
    k,
    base + Max(major, minor),
    [
      trace([
        [0, top + taper],
        [taper, top],
        [m, top],
        [m + shoulder, top - shoulder],
        [m + shoulder, r],
        [m + shoulder + r, 0, m + shoulder, 0],
        [M - shoulder - r, 0],
        [M - shoulder, r, M - shoulder, 0],
        [M - shoulder, top - shoulder],
        [M, top],
        [M, S + dr],
        [M + dr, S],
        [P - dr, S],
        [P, S + dr],
        [P, top],
        [v, top],
        [v, S + dr],
        [v + dr, S],
        [k - dr, S],
        [k, S + dr],
        [k, top],
        [k, base],
        [k, base + minor],
        [v + y, base + minor],
        [v + y, base],
        [v - y, base],
        [v - y, base + major],
        [P + y, base + major],
        [P + y, base],
        [P - y, base],
        [P - y, base + minor],
        [M + y, base + minor],
        [M + y, base],
        [M - y, base],
        [M - y, base + major],
        [m, base + major],
        [m, base],
        [taper, base],
        [0, base - taper],
      ]),
    ],
    [
      ...[m, M, P, v].map(x => line(x, top, x, base)),
      line(m, top, M, top),
      line(M, top, P, top),
      line(v, top, k, top),
      line(m, base, M - y, base),
      line(M + y, base, P - y, base),
      line(P + y, base, v - y, base),
      line(v + y, base, k, base),
      line(M - y, base, M - y - major, base + major),
      line(v - y, base, v - y - major, base + major),
    ]
  )
}

// Sealed-End Carton: four glued flaps at either end, the dust flaps cut back
// at their corners
const bxsc = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const relief = Max(DieCutStudioBoard.thickness, 0.8)
  const seal = W / 2
  const dust = Min(seal * 0.9, L / 2 - relief)
  const glue = clamp(W * 0.6, 8, 16)
  const chamfer = Min(dust * 0.4, W * 0.2)
  const i = relief / 2
  const [f, d, x, y, p] = [glue, glue + L, glue + L + W, glue + 2 * L + W, glue + 2 * L + 2 * W]
  const [top, base] = [seal, seal + H]
  const [v, k] = [top - dust, base + dust]
  const sealed_top = (a: number, b: number): Traced[] => [
    [a, top],
    [a, 0],
    [b, 0],
    [b, top],
  ]
  const dust_top = (a: number, b: number): Traced[] => [
    [a, top],
    [a, v + chamfer],
    [a + chamfer, v],
    [b - chamfer, v],
    [b, v + chamfer],
    [b, top],
  ]
  const sealed_bottom = (a: number, b: number): Traced[] => [
    [b, base],
    [b, base + seal],
    [a, base + seal],
    [a, base],
  ]
  const dust_bottom = (a: number, b: number): Traced[] => [
    [b, base],
    [b, k - chamfer],
    [b - chamfer, k],
    [a + chamfer, k],
    [a, k - chamfer],
    [a, base],
  ]
  return drawn(
    p,
    base + seal,
    [
      trace([
        [0, top],
        [f, top],
        ...sealed_top(f, d - i),
        ...dust_top(d + i, x - i),
        ...sealed_top(x + i, y - i),
        ...dust_top(y + i, p),
        [p, base],
        ...dust_bottom(y + i, p),
        ...sealed_bottom(x + i, y - i),
        ...dust_bottom(d + i, x - i),
        ...sealed_bottom(f, d - i),
        [f, base],
        [0, base],
      ]),
    ],
    [
      ...[f, d, x, y].map(at => line(at, top, at, base)),
      ...[
        [f, d],
        [d, x],
        [x, y],
        [y, p],
      ].flatMap(([a, b]) => [line(a!, top, b!, top), line(a!, base, b!, base)]),
    ]
  )
}

// Open Tray: the base, a wall off every side, the end walls' flaps glued
// inside the side ones
const bxot = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(width), mm(height), mm(depth)]
  const relief = Max(DieCutStudioBoard.thickness, 1)
  const flap = clamp(H * 0.8, 5, L / 2 - relief)
  const r = Min(flap * 0.4, 6)
  const [u, i, h, f, d] = [relief, H, H + L, 2 * H + L - relief, 2 * H + L]
  const [y, p, m] = [H, H + W, 2 * H + W]
  const [M, P] = [H - flap, H + W + flap]
  return drawn(
    d,
    m,
    [
      trace([
        [i, 0],
        [h, 0],
        [h, y],
        [h, M],
        [f - r, M],
        [f, M + r],
        [f, y],
        [d, y],
        [d, p],
        [f, p],
        [f, P - r],
        [f - r, P],
        [h, P],
        [h, p],
        [h, m],
        [i, m],
        [i, p],
        [i, P],
        [u + r, P],
        [u, P - r],
        [u, p],
        [0, p],
        [0, y],
        [u, y],
        [u, M + r],
        [u + r, M],
        [i, M],
        [i, y],
      ]),
    ],
    [
      line(i, y, h, y),
      line(i, p, h, p),
      line(i, y, i, p),
      line(h, y, h, p),
      line(u, y, i, y),
      line(u, p, i, p),
      line(h, y, f, y),
      line(h, p, f, p),
    ]
  )
}

// Hinged-Lid Tray: the open tray, its back wall going on into a lid and the
// lid's tuck
const bxht = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(width), mm(height), mm(depth)]
  const relief = Max(DieCutStudioBoard.thickness, 1)
  const flap = clamp(H * 0.8, 5, L / 2 - relief)
  const tuck = clamp(H * 0.7, 8, H - relief)
  const r = Min(flap * 0.4, 6)
  const u = Min(tuck * 0.5, L * 0.1)
  const lid = tuck + W
  const [f, d, x, y, p] = [relief, H, H + L, 2 * H + L - relief, 2 * H + L]
  const [M, P, v, k, w] = [tuck, lid, lid + H, lid + H + W, lid + 2 * H + W]
  const [g, C] = [v - flap, k + flap]
  return drawn(
    p,
    w,
    [
      trace([
        [d + u, 0],
        [x - u, 0],
        [x, M],
        [x, v],
        [x, g],
        [y - r, g],
        [y, g + r],
        [y, v],
        [p, v],
        [p, k],
        [y, k],
        [y, C - r],
        [y - r, C],
        [x, C],
        [x, k],
        [x, w],
        [d, w],
        [d, k],
        [d, C],
        [f + r, C],
        [f, C - r],
        [f, k],
        [0, k],
        [0, v],
        [f, v],
        [f, g + r],
        [f + r, g],
        [d, g],
        [d, v],
        [d, M],
      ]),
    ],
    [
      line(d, v, x, v),
      line(d, k, x, k),
      line(d, v, d, k),
      line(x, v, x, k),
      line(f, v, d, v),
      line(f, k, d, k),
      line(x, v, y, v),
      line(x, k, y, k),
      line(d, P, x, P),
      line(d, M, x, M),
    ]
  )
}

// Regular Slotted Container: four flaps top and bottom off every wall, as
// deep as half the side, slotted between
const bxrsc = (width: Distance, height: Distance, depth: Distance) => {
  const [L, W, H] = [mm(height), mm(depth), mm(width)]
  const glue = clamp(Min(L, W) * 0.25, 28, 45)
  const flap = W / 2
  const l = Max(DieCutStudioBoard.thickness, 3) / 2
  const [n, u, i, h, f] = [glue, glue + L, glue + L + W, glue + 2 * L + W, glue + 2 * L + 2 * W]
  const [top, base, bottom] = [flap, flap + H, 2 * flap + H]
  const walls: [number, number, boolean, boolean][] = [
    [n, u, true, false],
    [u, i, false, false],
    [i, h, false, false],
    [h, f, false, true],
  ]
  const above = ([a, b, first, last]: [number, number, boolean, boolean]): Traced[] => [
    [first ? a : a + l, top],
    [first ? a : a + l, 0],
    [last ? b : b - l, 0],
    [last ? b : b - l, top],
  ]
  const below = ([a, b, first, last]: [number, number, boolean, boolean]): Traced[] => [
    [last ? b : b - l, base],
    [last ? b : b - l, bottom],
    [first ? a : a + l, bottom],
    [first ? a : a + l, base],
  ]
  return drawn(
    f,
    bottom,
    [
      trace([
        [0, top],
        [n, top],
        ...walls.flatMap(above),
        [f, base],
        ...[...walls].reverse().flatMap(below),
        [n, base],
        [0, base],
      ]),
    ],
    [
      ...[n, u, i, h].map(x => line(x, top, x, base)),
      ...walls.flatMap(([a, b]) => [line(a, top, b, top), line(a, base, b, base)]),
    ]
  )
}

// Envelope (pointed): a flap off every side of the face, on a gusset as deep
// as the envelope, the top one pointed
const envb = (width: Distance, height: Distance, depth: Distance) => {
  const [t, s, g] = [mm(width), mm(height), mm(depth)]
  const side = clamp(t * 0.32, 10, t * 0.46)
  const above = clamp(s * 0.52, 20, s * 0.85)
  const below = clamp(s * 0.5, 15, s * 0.7)
  const u = clamp(side, 3, s / 2 - 3)
  const h = clamp(side * 0.6, 3, t / 2 - 8)
  const [x0, y0] = [side + g, above + g]
  const [x1, y1] = [x0 + t, y0 + s]
  const [right, bottom] = [x1 + g + side, y1 + g + below]
  return drawn(
    right,
    bottom,
    [
      trace([
        [x0, y0],
        [x0, y0 - g],
        [x0 + t / 2, 0],
        [x1, y0 - g],
        [x1, y0],
        [x1 + g, y0],
        [right, y0 + u],
        [right, y1 - u],
        [x1 + g, y1],
        [x1, y1],
        [x1, y1 + g],
        [x1 - h, bottom],
        [x0 + h, bottom],
        [x0, y1 + g],
        [x0, y1],
        [x0 - g, y1],
        [0, y1 - u],
        [0, y0 + u],
        [x0 - g, y0],
      ]),
    ],
    face_creases([x0, x1, y0, y1], 0, g)
  )
}

// A wallet or catalog envelope: a flap off every side of the face, on a
// gusset as deep as the envelope, each its share of the face deep, inset
// and round at its corners
const envelope =
  (top: number, side: number, bottom: number) =>
  (width: Distance, height: Distance, depth: Distance) => {
    const [a, n, g] = [mm(width), mm(height), mm(depth)]
    const [r, l, u] = [n * top, a * side, n * bottom]
    const i = clamp(5, 0, Min(l, r, u) * 0.4)
    const h = clamp(10, 2, Min(r, l, u) * 0.8)
    const [x0, y0] = [l + g, r + g]
    const [x1, y1] = [x0 + a, y0 + n]
    const [right, y] = [x1 + g + l, y1 + g + u]
    return drawn(
      right,
      y,
      [
        trace([
          [x0, y0],
          [x0, y0 - g],
          [x0 + i, h],
          [x0 + i + h, 0, x0 + i, 0],
          [x1 - i - h, 0],
          [x1 - i, h, x1 - i, 0],
          [x1, y0 - g],
          [x1, y0],
          [x1 + g, y0],
          [right - h, y0 + i],
          [right, y0 + i + h, right, y0 + i],
          [right, y1 - i - h],
          [right - h, y1 - i, right, y1 - i],
          [x1 + g, y1],
          [x1, y1],
          [x1, y1 + g],
          [x1 - i, y - h],
          [x1 - i - h, y, x1 - i, y],
          [x0 + i + h, y],
          [x0 + i, y - h, x0 + i, y],
          [x0, y1 + g],
          [x0, y1],
          [x0 - g, y1],
          [h, y1 - i],
          [0, y1 - i - h, 0, y1 - i],
          [0, y0 + i + h],
          [h, y0 + i, 0, y0 + i],
          [x0 - g, y0],
        ]),
      ],
      face_creases([x0, x1, y0, y1], 0, g)
    )
  }

export const DieCutStudioDieList = RecordByPrimaryKey([
  die("bxte", [MM(50), MM(50)], bxte),
  die("bxrt", [MM(50), MM(50)], bxrt),
  die("bxab", [MM(50), MM(50)], bxab),
  die("bxtab", [MM(50), MM(50)], bxtab),
  die("bxsl", [MM(50), MM(50)], bxsl),
  die("bxsc", [MM(50), MM(50)], bxsc),
  die("bxot", [MM(50), MM(50)], bxot),
  die("bxht", [MM(50), MM(50)], bxht),
  die("bxrsc", [MM(50), MM(50)], bxrsc),
  die("bxgb", [MM(50), MM(50)], bxgb),
  die("bxhx", [MM(50), MM(50)], bxhx),
  die("envp", [MM(162), MM(114)], envelope(50 / 229, 20 / 162, 190 / 229)),
  die("envd", [MM(162), MM(114)], envd),
  die("envc", [MM(162), MM(114)], envelope(50 / 229, 85 / 162, 35 / 229)),
  die("envb", [MM(162), MM(114)], envb),
] as const)
